'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X, AlertCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

import { AppImage } from '@/components/common/AppImage';
import { formatCurrency } from '@/lib/utils/format';
import type { Player, Team } from '@/lib/types/auction';

interface PlayerStatusNotificationProps {
  player: Player;
  team?: Team;
  amount?: number;
  status: 'sold' | 'unsold';
  onClose: () => void;
  autoCloseDelay?: number; // in milliseconds
}

export function PlayerStatusNotification({
  player,
  team,
  amount,
  status,
  onClose,
  autoCloseDelay = 15000, // 15 seconds default
}: PlayerStatusNotificationProps) {
  const [countdown, setCountdown] = useState(Math.floor(autoCloseDelay / 1000));
  const notificationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(onClose, autoCloseDelay);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(countdownInterval);
    };
  }, [onClose, autoCloseDelay]);

  const playerName = player?.user?.name || 'Unknown Player';
  const playerImage = player?.user?.profilePicture || '';
  const teamName = team?.name || 'Unknown Team';
  const teamLogo = team?.logo || '';

  // Memoized Confetti Generation (Client-side only to prevent hydration mismatch)
  const [confettiElements, setConfettiElements] = useState<
    Array<{
      id: number;
      x: number;
      y: number;
      delay: number;
      duration: number;
      rotate: number;
      scale: number;
      colorClass: string;
    }>
  >([]);

  useEffect(() => {
    if (status === 'unsold') return;

    const width = 400; // Default width
    const height = 300; // Default height

    const elements = Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      x: Math.random() * width,
      y: Math.random() * -height,
      delay: Math.random() * 1.5,
      duration: Math.random() * 3 + 2,
      rotate: Math.random() * 360,
      scale: Math.random() * 0.7 + 0.5,
      colorClass: ['bg-yellow-300', 'bg-green-400', 'bg-white'][i % 3],
    }));

    setConfettiElements(elements);
  }, [status]); // Generate when status changes

  const statusConfig = {
    sold: {
      title: 'SOLD!',
      icon: CheckCircle,
      iconColor: 'text-green-500',
      gradient: 'from-primary-600 to-accent-blue',
    },
    unsold: {
      title: 'UNSOLD',
      icon: AlertCircle,
      iconColor: 'text-yellow-500',
      gradient: 'from-gray-700 to-gray-900',
    },
  };

  const config = statusConfig[status];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 25,
          },
        }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      >
        <motion.div
          ref={notificationRef}
          initial={{ y: 50, opacity: 0 }}
          animate={{
            y: 0,
            opacity: 1,
            transition: {
              delay: 0.2,
              type: 'spring',
              stiffness: 300,
              damping: 25,
            },
          }}
          className={`relative w-full max-w-lg overflow-hidden rounded-2xl bg-gradient-to-br ${config.gradient} p-6 shadow-2xl`}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-1 text-white/80 transition-colors hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Status badge - Fixed animation to use only 2 keyframes */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
              transition: {
                delay: 0.4,
                type: 'spring',
                stiffness: 200,
                damping: 20,
              },
            }}
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg"
          >
            <config.icon className={`h-12 w-12 ${config.iconColor}`} />
          </motion.div>

          {/* Status text */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.6 } }}
            className="mb-2 text-center text-3xl font-bold text-white"
          >
            {config.title}
          </motion.h2>

          {/* Player details */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-white/70 shadow-md">
                <AppImage
                  src={playerImage}
                  alt={playerName}
                  gradient
                  size="md"
                  fallbackText={playerName}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {playerName}
                </h3>
                <p className="text-sm capitalize text-white/80">
                  {player?.role || 'Player'}
                </p>
              </div>
            </div>

            {/* Team details - Only show for sold status */}
            {status === 'sold' && team && (
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <h3 className="text-xl font-semibold text-white">
                    {teamName}
                  </h3>
                  <p className="text-sm text-white/80">New Owner</p>
                </div>
                <div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-white/70 shadow-md">
                  <AppImage
                    src={teamLogo}
                    alt={teamName}
                    gradient
                    fallbackText={teamName}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sale amount - Only show for sold status */}
          {status === 'sold' && amount && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1,
                transition: { delay: 0.8, type: 'spring' },
              }}
              className="mt-6 rounded-xl bg-white/10 p-4 text-center"
            >
              <p className="text-sm font-medium text-white/80">
                Final Bid Amount
              </p>
              <p className="text-3xl font-bold text-yellow-300">
                {formatCurrency(amount)}
              </p>
            </motion.div>
          )}

          {/* Countdown Timer */}
          <div className="mt-6 text-center text-sm text-white/70">
            <p>Continuing to next player in {countdown}s</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/20">
              <motion.div
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: autoCloseDelay / 1000, ease: 'linear' }}
                className="h-full bg-yellow-300"
              />
            </div>
          </div>

          {/* Confetti animation - Fixed to use only 2 keyframes */}
          {status === 'sold' && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {confettiElements.map(
                ({ id, x, y, delay, duration, rotate, scale, colorClass }) => (
                  <motion.div
                    key={id}
                    initial={{
                      x: `${x}px`,
                      y: `${y}px`,
                      opacity: 1,
                      scale,
                      rotate: 0,
                    }}
                    animate={{
                      y: y + 200,
                      opacity: 0,
                      rotate: rotate,
                    }}
                    transition={{
                      duration,
                      delay,
                      repeat: Infinity,
                      ease: 'easeOut',
                    }}
                    className={`absolute h-3 w-3 rounded-full ${colorClass}`}
                  />
                )
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
